import { Stack } from "@mui/material";
import PageContainer from "components/PageContainer/PageContainer";
import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";

const UNAUTHORIZED_STATE_INIT_VALUE = false;
const ERROR_STATE_INIT_VALUE = null;

/**
 * @typedef {ReturnType<typeof usePageWrapper>} UsePageWrapperReturn
 */

/**
 * @typedef callApisReturnType
 * @property {boolean} isCalled 
 * @property {boolean} isSuccess - true if status code is in allowedStatusCodes and response code is in allowedResCodes
 * @property {boolean} isError - true if both codes are not in allowedStatusCodes and allowedResCodes, and not in unauthorizedStatusCodes and unauthorizedResCodes
 * @property {number} [statusCode] - The status code extracted from AxiosResponse.status
 * @property {string} [responseCode] - The response code extracted from AxiosResponse.data.responseCode
 * @property {any} [response] - The entire response extracted from AxiosResponse.data
 * @property {any} [data] - The data extracted from AxiosResponse.data.data
 */
/**
 * @template {string} K
 * @typedef callApisEntryType
 * @property {(any) => Promise<import("axios").AxiosResponse<any>>} api
 * @property {any} payload
 * @property {any[]} [params]  - use when api function accepts more than one argument
 * @property {K} [partitionKeys] - provide affected partitions for this api call
 * @property {boolean} skipCall - skip calling the api, default: false
 * @property {number[]} [allowedStatusCodes = [200]] - check if status code is in this array, default: [200]
 * @property {number[]} [unauthorizedStatusCodes = [403]] - check if status code is in this array, default: [403]
 * @property {string[]} [allowedResCodes = ["0000"]] - check if response code is in this array, default: ["0000"]
 * @property {string[]} [unauthorizedResCodes = []] - check if response code is in this array, default: []
 * @property {boolean} [toastOnError = false] - show a generic toast when api is non-success and non-unauthorized with the response error message, default: false
 * @property {boolean} [blockOnError = true] - enable a generic error UI cover when api is non-success and non-unauthorized, default: true
 * @property {boolean} [toastOnUnauthorized = true] - show a generic toast when unauthorized conditions are met, default: true
 * @property {boolean} [blockOnUnauthorized = true] - show a generic unauthorized UI cover (currently points to the error UI) when unauthorized conditions are met, default: true
 * @property {(result: callApisReturnType, setters: customErrorHandlerSettersType)} [customErrorHandler] - allow custom error handling
 */
/**
 * @template {string} K
 * @typedef {(apis: callApisEntryType<readonly K[]>[]) => Promise<callApisReturnType[]>} CallApisFn
 */

/**
 * Custom hook to faciliate api error handling and using PageContainer
 * @template {string} PartitionKey
 * @param {Object} props
 * @param {readonly PartitionKey[]} [props.partitionKeys]
 */
const usePageWrapper = ({ partitionKeys } = {}) => {
  const isPartitioned = !!partitionKeys?.length;

  const { logout } = useAuth();
  const { t } = useTranslation("translation", { keyPrefix: 'component.pageWrapper' });
  const { snackbarError } = useSnackbarMAS();

  /** @type {React.RefObject<HTMLDivElement>} */
  const pageheaderRef = useRef(null);
  const [isUnauthorized, setIsUnauthorizedState] = useState(isPartitioned ? Object.fromEntries(partitionKeys.map((key) => [key, UNAUTHORIZED_STATE_INIT_VALUE])) : UNAUTHORIZED_STATE_INIT_VALUE);
  const setIsUnauthorized = (value, key) => {
    if (isPartitioned) {
      if (!key) {
        console.warn("PageWrapper: missing key in partitioned setIsUnauthorized");
        return;
      }
      setIsUnauthorizedState((prev) => ({ ...prev, [key]: value }))
    } else {
      setIsUnauthorizedState(value)
    }
  };

  const [error, setErrorState] = useState(isPartitioned ? Object.fromEntries(partitionKeys.map((key) => [key, ERROR_STATE_INIT_VALUE])) : ERROR_STATE_INIT_VALUE); // falsy = no error, true = default error, string = error message
  const setError = (value, key) => {
    if (isPartitioned) {
      if (!key) {
        console.warn("PageWrapper: missing key in partitioned setIsUnauthorized")
        return;
      };
      setErrorState((prev) => ({ ...prev, [key]: value }))
    } else {
      setErrorState(value)
    }
  };

  const networkErrorToast = useCallback(() => snackbarError({ message: t("toast.error.network") }), [snackbarError]);
  const errorToast = useCallback((message) => snackbarError({ message: t("toast.error", { message: message ?? t("toast.error.fallback") }) }), [snackbarError]);
  const unauthorizedToast = useCallback(() => snackbarError({ message: t("toast.unauthorized") }), [snackbarError]);

  /** Scroll to the page header  */
  const scrollToHeader = useCallback(() => pageheaderRef.current?.scrollIntoView({ behavior: "smooth" }), []);

  /** 
   * Clear error covers
   * @param {PartitionKey} [key] - partition key if partitioned
   */
  const clearError = (key) => {
    setIsUnauthorized(UNAUTHORIZED_STATE_INIT_VALUE, key);
    setError(ERROR_STATE_INIT_VALUE, key);
  };

  /**
   * @type {CallApisFn<PartitionKey>}
   */
  const callApis = useCallback(
    async (apis) => {
      const defaultConfig = {
        skipCall: false,
        allowedStatusCodes: [200],
        unauthorizedStatusCodes: [403],
        allowedResCodes: ["0000"],
        unauthorizedResCodes: [],
        toastOnError: false,
        blockOnError: true,
        toastOnUnauthorized: true,
        blockOnUnauthorized: true,
        customErrorHandler: () => { },
      };
      const setters = {
        setIsUnauthorized,
        setError
      }
      const [...results] = await Promise.all(
        apis.map(({ api, payload, params, skipCall }) => {
          if (skipCall) return null;
          if (params) return api(...params);
          return api(payload);
        })
      );

      return results.map((res, i) => {
        if (res == null || !res?.status) {
          res !== null && networkErrorToast();
          return {
            isCalled: false,
            isSuccess: false,
            isError: false,
            statusCode: undefined,
            responseCode: undefined,
            response: undefined,
            data: undefined,
          };
        }

        const config = {
          ...defaultConfig,
          ...apis[i],
        }
        const {
          partitionKeys,
          allowedStatusCodes,
          unauthorizedStatusCodes,
          allowedResCodes,
          unauthorizedResCodes,
          toastOnError,
          blockOnError,
          toastOnUnauthorized,
          blockOnUnauthorized,
          customErrorHandler
        } = config;

        /** @type {callApisReturnType} */
        const result = {
          isCalled: true,
          isSuccess: false,
          isError: false,
          statusCode: res.status ?? undefined,
          responseCode: res.data?.responseCode ?? undefined,
          response: res.data ?? undefined,
          data: res.data?.data ?? undefined,
        }

        try {
          const status = res.status;
          const isInvalidToken = status === 403 && res.data?.errorCode === "INVALID_TOKEN";
          if ((status === 401) || isInvalidToken) {
            logout("TOKEN_EXPIRED");
            return result;
          }

          if (!allowedStatusCodes.includes(status)) {
            if (unauthorizedStatusCodes.includes(status)) {
              toastOnUnauthorized && unauthorizedToast();
              blockOnUnauthorized && (isPartitioned ? partitionKeys.forEach((key) => setIsUnauthorized(true, key)) : setIsUnauthorized(true));
              return result;
            } else {
              result.isError = true;
              toastOnError && errorToast(res?.message);
              blockOnError && (isPartitioned ? partitionKeys.forEach((key) => setError(true, key)) : setError(true));
              customErrorHandler(result, setters);
              return result;
            }
          }

          const resCode = res.data?.responseCode ?? null;
          if (!allowedResCodes.includes(resCode)) {
            if (unauthorizedResCodes.includes(resCode)) {
              toastOnUnauthorized && unauthorizedToast();
              blockOnUnauthorized && (isPartitioned ? partitionKeys.forEach((key) => setIsUnauthorized(true, key)) : setIsUnauthorized(true));
              return result;
            } else {
              result.isError = true;
              toastOnError && errorToast(res?.data?.message);
              blockOnError && (isPartitioned ? partitionKeys.forEach((key) => setError(true, key)) : setError(true));
              customErrorHandler(result, setters);
              return result;
            }
          }

          result.isSuccess = true;
          return result;

        } catch (e) {
          console.error(e);
          result.isError = true;
          return result;
        }
      });


    }, [setIsUnauthorized, setError]);

  /**
   * @typedef PageWrapperProps
   * @property {boolean} [noPadding=false]
   * @property {PartitionKey} [partitionKey] partition key
   * @property {boolean} [isLoading=false]
   * @property {import("react").ReactNode} children
   */
  /**
   * @type {import('react').FC<PageWrapperProps>}
   */
  const PageWrapper = useCallback(({ noPadding = false, partitionKey, isLoading = false, children }) => {
    const isBlocked = isPartitioned ? (error[partitionKey] || isUnauthorized[partitionKey]) : (error || isUnauthorized);
    return (
      <Stack sx={{ flexGrow: 1, position: "relative" }}>
        <PageContainer
          isLoading={!isBlocked && isLoading}
          noPadding={noPadding}
          // unauthorized={isPartitioned ? isUnauthorized[partitionKey] : isUnauthorized} //Note: error and unAuthorized will share the same ui cover for now
          errorMessage={isPartitioned ? (error[partitionKey] || isUnauthorized[partitionKey]) : (error || isUnauthorized)} //Note: error and unAuthorized will share the same ui cover for now
        >
          {children}
        </PageContainer>
      </Stack>
    );
  }, [isUnauthorized, error]);

  /**
   * @typedef PageOuterContainerProps
   * @property {import("react").ReactNode} children
   */
  /**
   * Use this when the immediate parent is MainLayout
   * 
   * @type {import('react').FC<PageOuterContainerProps>}
   */
  const PageOuterContainer = useCallback(({ children, ...props }) => {
    return (
      <Stack
        direction="column"
        {...props}
        sx={{
          height: "100%",
          width: "100%",
          background: "white",
          overflow: "auto",
          ...props?.sx
        }}
      >
        <Stack
          direction="column"
          sx={{
            flexGrow: 1,
            padding: "24px",
          }}>
          {children}
        </Stack>
      </Stack>
    );
  }, []);

  /**
   * @typedef PageStickyHeaderProps
   * @property {import("react").ReactNode} children
   * @property {boolean} [noBottomPadding=false]
   * @property {boolean} [noBorder=false]
   * @property {boolean} [noSticky=false]
   */
  /**
   * @type {import('react').FC<PageStickyHeaderProps>}
   */
  const PageStickyHeader = useCallback(({ children, noBottomPadding = false, noBorder = false, noSticky = false, ...props }) => {
    return (
      <Stack
        ref={pageheaderRef}
        direction="column"
        gap={2}
        {...props}
        sx={{
          m: "-24px -24px 24px",
          p: noBottomPadding ? "24px 24px 0px" : "24px",
          ...!noSticky && { position: "sticky" },
          top: 0,
          zIndex: 100,
          background: "white",
          borderBottom: noBorder ? "none" : "1px solid #E0E0E0",
          ...props?.sx
        }}
      >
        {children}
      </Stack>
    );
  }, []);

  /**
   * @typedef PageBodyProps
   * @property {import("react").ReactNode} children
   * @property {boolean} [noSidePadding=false]
  */
  /**
   * @type {import('react').FC<PageBodyProps>}
   */
  const PageBody = useCallback(({ children, noSidePadding = false, ...props }) => {
    return (
      <Stack
        direction="column"
        gap={2}
        {...props}
        sx={{
          p: noSidePadding ? "0px" : "0px 24px",
          ...props?.sx
        }}
      >
        {children}
      </Stack>
    );
  }, []);

  /**
   * @typedef PageStickyFooterProps
   * @property {import("react").ReactNode} children
   * @property {boolean} [noPadding=false]
   * 
   */
  /**
   * @type {import('react').FC<PageStickyFooterProps>}
   */
  const PageStickyFooter = useCallback(({ children, noPadding = false, ...props }) => {
    return (
      <Stack
        direction="column"
        justifyContent="stretch"
        gap={2}
        {...props}
        sx={{
          m: "0px -24px -24px",
          p: noPadding ? "0 24px 24px" : "24px",
          position: "sticky",
          bottom: 0,
          zIndex: 1,
          background: "white",
          ...props?.sx
        }}
      >
        {children}
      </Stack>
    );
  }, []);

  return {
    callApis,
    PageWrapper,
    PageOuterContainer,
    PageStickyHeader,
    PageBody,
    PageStickyFooter,

    scrollToHeader,
    clearError
  }
}

export default usePageWrapper